import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { importEncoderCsv } from '../api/encoderApi';
import { isAdminSession } from '../../../shared/auth/session';

const today = () => new Date().toISOString().slice(0, 10);

const METHOD_OPTIONS = [
  { value: 'trimmedMean', label: 'Media (descarta el intento más rápido y el más lento)' },
  { value: 'median', label: 'Mediana simple (valor central de las 5 repeticiones)' },
];

const methodLabel = (value) => METHOD_OPTIONS.find((option) => option.value === value)?.label ?? value;

/** Extrae día y mes de un nombre de archivo tipo "adrencoder_0906.csv" (DDMM antes de .csv). */
const parseDateFromFilename = (filename) => {
  const match = filename.match(/(\d{2})(\d{2})\.csv$/i);
  if (!match) return null;

  const [, day, month] = match;
  const dayNum = Number(day);
  const monthNum = Number(month);
  if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12) return null;

  return { day, month };
};

export default function ImportEncoderCsv() {
  const navigate = useNavigate();
  const isAdmin = isAdminSession();
  const [date, setDate] = useState(today());
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const [method, setMethod] = useState('trimmedMean');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  const [batchYear, setBatchYear] = useState(String(new Date().getFullYear()));
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchMethod, setBatchMethod] = useState('trimmedMean');
  const batchInputRef = useRef(null);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchProgress, setBatchProgress] = useState('');
  const [batchError, setBatchError] = useState('');
  const [batchResults, setBatchResults] = useState([]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/home', { replace: true });
    }
  }, [isAdmin, navigate]);

  const batchEntries = useMemo(() => {
    const entries = batchFiles.map((batchFile) => {
      const parsed = parseDateFromFilename(batchFile.name);
      const dateStr = parsed ? `${batchYear}-${parsed.month}-${parsed.day}` : null;
      return { file: batchFile, dateStr, valid: Boolean(parsed) };
    });

    return entries.sort((a, b) => {
      if (a.dateStr && b.dateStr) return a.dateStr.localeCompare(b.dateStr);
      return 0;
    });
  }, [batchFiles, batchYear]);

  const handleSubmit = async () => {
    if (submitting || !isAdmin) return;
    if (!file) {
      setError('Debe seleccionar el archivo CSV del ADR.');
      return;
    }
    if (!date) {
      setError('Debe indicar la fecha de la sesión.');
      return;
    }

    try {
      setError('');
      setSubmitting(true);
      const result = await importEncoderCsv({ file, date, method });
      setReport(result);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (importError) {
      console.error('Error importando CSV de encoder', importError);
      setError(importError?.response?.data?.message || 'No se pudo importar el archivo. Intente nuevamente.');
      setReport(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBatchSubmit = async () => {
    if (batchSubmitting || !isAdmin) return;

    const validEntries = batchEntries.filter((entry) => entry.valid);
    if (validEntries.length === 0) {
      setBatchError('Seleccione al menos un archivo cuyo nombre siga el patrón "..._DDMM.csv" (ej. adrencoder_0906.csv).');
      return;
    }

    setBatchError('');
    setBatchResults([]);
    setBatchSubmitting(true);

    const results = [];
    for (const entry of validEntries) {
      setBatchProgress(entry.file.name);
      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await importEncoderCsv({ file: entry.file, date: entry.dateStr, method: batchMethod });
        results.push({
          fileName: entry.file.name, date: entry.dateStr, success: true, report: result,
        });
      } catch (importError) {
        console.error(`Error importando ${entry.file.name}`, importError);
        results.push({
          fileName: entry.file.name,
          date: entry.dateStr,
          success: false,
          message: importError?.response?.data?.message || 'No se pudo importar el archivo.',
        });
      }
    }

    setBatchResults(results);
    setBatchProgress('');
    setBatchSubmitting(false);
    setBatchFiles([]);
    if (batchInputRef.current) {
      batchInputRef.current.value = '';
    }
  };

  if (!isAdmin) return null;

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" gutterBottom color="#0D6AEE">
          Importar CSV del Encoder (ADR)
        </Typography>
        <Typography variant="body2">
          Sube el CSV diario exportado por el dispositivo ADR. El sistema descarta el intento más lento y el
          más rápido (fallo de técnica o desvío) y calcula la media de velocidad, distancia y potencia de las
          3 repeticiones restantes de cada serie, completando los registros de Encoder que ya fueron creados
          ese día para cada paciente (según el número de serie indicado en su código de prueba).
          No crea pacientes ni registros nuevos.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            label="Fecha de la sesión"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl sx={{ minWidth: 260 }}>
            <InputLabel id="import-method-label">Método de cálculo</InputLabel>
            <Select
              labelId="import-method-label"
              label="Método de cálculo"
              value={method}
              onChange={(event) => setMethod(event.target.value)}
            >
              {METHOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" component="label">
            {file ? file.name : 'Seleccionar CSV'}
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              ref={fileInputRef}
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </Button>
          <Button variant="contained" color="primary" disabled={submitting} onClick={handleSubmit}>
            {submitting ? 'Importando...' : 'Importar'}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" gutterBottom color="#0D6AEE">
          Importar en lote
        </Typography>
        <Typography variant="body2">
          Selecciona varios CSV cuyo nombre siga el patrón "..._DDMM.csv" (ej. adrencoder_0906.csv = 09/06).
          Indica el año de las sesiones y el sistema importará cada archivo con su fecha correspondiente,
          en orden cronológico.
        </Typography>
        {batchError && <Alert severity="error">{batchError}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            label="Año"
            type="number"
            value={batchYear}
            onChange={(event) => setBatchYear(event.target.value)}
            sx={{ width: 120 }}
          />
          <FormControl sx={{ minWidth: 260 }}>
            <InputLabel id="batch-import-method-label">Método de cálculo</InputLabel>
            <Select
              labelId="batch-import-method-label"
              label="Método de cálculo"
              value={batchMethod}
              onChange={(event) => setBatchMethod(event.target.value)}
            >
              {METHOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" component="label">
            {batchFiles.length > 0 ? `${batchFiles.length} archivo(s) seleccionado(s)` : 'Seleccionar CSVs'}
            <input
              type="file"
              accept=".csv,text/csv"
              multiple
              hidden
              ref={batchInputRef}
              onChange={(event) => setBatchFiles(Array.from(event.target.files ?? []))}
            />
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={batchSubmitting || batchEntries.length === 0}
            onClick={handleBatchSubmit}
          >
            {batchSubmitting ? 'Importando...' : `Importar ${batchEntries.filter((entry) => entry.valid).length} archivo(s)`}
          </Button>
        </Stack>

        {batchSubmitting && (
          <Alert severity="info">Importando {batchProgress}…</Alert>
        )}

        {batchEntries.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Archivo</TableCell>
                  <TableCell>Fecha detectada</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchEntries.map((entry) => (
                  <TableRow key={entry.file.name}>
                    <TableCell>{entry.file.name}</TableCell>
                    <TableCell>{entry.dateStr ?? '—'}</TableCell>
                    <TableCell>
                      {entry.valid
                        ? <Chip size="small" label="Listo" color="success" variant="outlined" />
                        : <Chip size="small" label="Nombre no reconocido" color="warning" variant="outlined" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {batchResults.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Archivo</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Resultado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batchResults.map((result) => (
                  <TableRow key={result.fileName}>
                    <TableCell>{result.fileName}</TableCell>
                    <TableCell>{result.date}</TableCell>
                    <TableCell>
                      {result.success
                        ? `${result.report.totalSeriesEnCsv} series · ${result.report.updated.length} actualizados`
                        : <Chip size="small" label={result.message} color="error" variant="outlined" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {report && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Resultado — {report.date}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {report.totalSeriesEnCsv} series detectadas en el CSV · {report.updated.length} registros actualizados
            {report.method && ` · Método: ${methodLabel(report.method)}`}
          </Typography>

          {report.updated.length > 0 && (
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Paciente</TableCell>
                    <TableCell>Código prueba</TableCell>
                    <TableCell>Velocidad</TableCell>
                    <TableCell>Potencia</TableCell>
                    <TableCell>Distancia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.updated.map((row) => (
                    <TableRow key={row.codigo}>
                      <TableCell>{row.pacienteNombre ?? `#${row.pacienteId}`}</TableCell>
                      <TableCell>{row.codigoPrueba}</TableCell>
                      <TableCell>{row.before.velocidad} → {row.after.velocidad}</TableCell>
                      <TableCell>{row.before.potencia} → {row.after.potencia}</TableCell>
                      <TableCell>{row.before.distancia} → {row.after.distancia}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {report.sinCoincidenciaEnCsv.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {report.sinCoincidenciaEnCsv.length} registro(s) de Encoder de ese día no tienen una serie
              correspondiente completa en el CSV (código de prueba sin número de serie válido, o serie con
              menos de 5 repeticiones): {report.sinCoincidenciaEnCsv.map((r) => r.codigoPrueba || `#${r.codigo}`).join(', ')}
            </Alert>
          )}

          {report.seriesSinRegistro.length > 0 && (
            <Alert severity="info">
              El CSV trae {report.seriesSinRegistro.length} serie(s) sin un registro de Encoder creado ese día:
              {' '}{report.seriesSinRegistro.map((s) => `S${s.series}`).join(', ')}. Cree el registro del paciente
              correspondiente y vuelva a importar el CSV para completarlo.
            </Alert>
          )}
        </Paper>
      )}
    </Stack>
  );
}
